<script setup lang="ts">
/**
 * 〈站台詳情〉（`/hosts/:id/sites/:siteId`；operator 以上，批151 C7）。
 *
 * 從主機詳情的站台列與〈全部站台〉進來。一頁看完一個代管站：站台本身、所在主機與客戶、
 * 目前生效的證、子證與合併後配額、產品回報的指標、站台機密。
 *
 * ## 權限分塊（後端 `GET /sites/{id}` 已依角色收掉，前端照 `sections` 畫）
 *
 * | 區塊 | operator | admin | 營運主管以上 |
 * |---|---|---|---|
 * | 站台、主機與客戶、回報指標 | 看 | 看 | 看 |
 * | 目前的證、子證與合併後配額 | 「需要 admin 以上權限」 | 看＋同期限改內容＋簽發子證 | 同左 |
 * | 站台機密 | 「需要 admin 以上權限」 | 只看（已設定／末四碼／套用狀態） | 設定／更換／清除／重送／清除站上殘留／只釋出 |
 *
 * operator 拿到的那三塊是 `null`——畫面寫「需要 admin 以上權限」而不是留白（留白會被讀成「沒資料」）。
 *
 * ## 合併後配額
 *
 * 後端 `effective` 已依產品宣告的合併方式算好（與〈授權〉清單、客戶主機頁同一支），這裡**直接顯示**，
 * 不在前端重算；「＋加購」的來源到期日照既有 `lib/licenseLabels.ts`。
 */
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import AddonIssueModal from '@/components/AddonIssueModal.vue'
import QuotaChips from '@/components/QuotaChips.vue'
import SiteContentModal from '@/components/SiteContentModal.vue'
import SiteSecretModal from '@/components/SiteSecretModal.vue'
import UiButton from '@/components/UiButton.vue'
import type { License, LicenseAddon } from '@/api/ls'
import * as sitesApi from '@/api/sites'
import type {
  SiteDetail,
  SiteLicenseContentResult,
  SiteSecretField,
  SiteSecretWriteResult,
} from '@/api/sites'
import type { HostJob } from '@/api/hosting'
import { healthLabel, hostStatusLabel, siteStateClass, siteStateLabel, tlsStateLabel } from '@/lib/fleet'
import {
  addonSourceLabel,
  addonStatusLabel,
  addonSummary,
  issuedViaLabel,
  moduleAddonHint,
} from '@/lib/licenseLabels'
import { buildMetricRows } from '@/lib/metrics'
import { effectiveStatus, fmtModulesZh, statusLabel } from '@/lib/modules'
import { planLabel } from '@/lib/quotas'
import { CONTENT_FIELD_LABELS, type ContentField } from '@/lib/siteContent'
import {
  apiDetail,
  clearResidueConfirmMessage,
  releaseConfirmMessage,
  secretAppliedText,
  secretLast4Text,
  secretRowActions,
  secretVersionText,
  siteSecretErrText,
} from '@/lib/siteSecrets'
import { fmtSeen } from '@/lib/staleness'
import { formatRemaining, formatTerm } from '@/lib/terms'
import { usePolling } from '@/lib/poll'
import { safeHttpUrl } from '@/lib/safeUrl'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const route = useRoute()
const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()

const siteId = computed(() => String(route.params.siteId ?? ''))
const detail = ref<SiteDetail | null>(null)
const loading = ref(true)
const loadError = ref('')

async function load() {
  if (!siteId.value) return
  try {
    detail.value = await sitesApi.getSite(siteId.value)
    loadError.value = ''
  } catch (e) {
    const status = (e as { status?: number }).status
    loadError.value = status === 404 ? '找不到這個站台（可能已被移除）。' : '讀取站台詳情失敗'
  } finally {
    loading.value = false
  }
}

watch(siteId, () => {
  detail.value = null
  loading.value = true
  lastJob.value = null
  void load()
})
void store.ensureProducts().catch(() => {})

/**
 * 輪詢（`usePolling`：切走分頁自動暫停）：第一輪＝初次載入；之後站台機密有「尚未套用」的欄位
 * （剛派出寫入任務）時每 15 秒重抓，否則每 4 輪（約 60 秒）一次（指標與狀態）。
 */
const pendingApply = computed(() =>
  (detail.value?.site_secrets?.fields ?? []).some((f) => f.configured && !f.applied),
)
let tick = 0
usePolling(async () => {
  const n = tick++
  if (n === 0 || pendingApply.value || n % 4 === 0) await load()
}, 15_000)

const site = computed(() => detail.value?.site ?? null)
const spec = computed(() => store.productByKey(site.value?.product))
const productLabel = computed(() => spec.value?.name || site.value?.product || '—')
const lic = computed(() => detail.value?.license ?? null)
const effective = computed(() => detail.value?.effective ?? null)
const addons = computed(() => detail.value?.addons ?? [])
const secrets = computed(() => detail.value?.site_secrets ?? null)
const sections = computed(() => detail.value?.sections ?? { license: false, addons: false, site_secrets: false })

/** 站台還「在役」嗎（回收中／已回收不能再改內容或設定機密）。 */
const siteLive = computed(() => !!site.value && !['decommissioning', 'decommissioned'].includes(site.value.state))

// --- 目前的證 ---
const licModules = computed(() => (lic.value ? fmtModulesZh(spec.value, lic.value.modules) : []))
/** 行為參數：只列產品宣告過的鍵（值缺席＝預設），未宣告但證上有的鍵原樣列在後面。 */
const policyRows = computed(() => {
  const values = lic.value?.policy ?? {}
  const declared = spec.value?.policies ?? []
  const rows = declared
    .filter((d) => !d.deprecated || values[d.key] !== undefined)
    .map((d) => {
      const raw = values[d.key]
      const has = raw !== undefined && raw !== null
      return { key: d.key, label: d.label, text: has ? String(raw) : `${d.absent_default ?? '—'}（預設）`, custom: has }
    })
  const known = new Set(declared.map((d) => d.key))
  for (const [k, v] of Object.entries(values)) {
    if (!known.has(k)) rows.push({ key: k, label: k, text: String(v), custom: true })
  }
  return rows
})
function termText(l: { term_days: number | null; term_minutes: number }): string {
  return formatTerm(l.term_days ?? 0, l.term_minutes ?? 0) || '—'
}

// --- 子證與合併後 ---
const effModules = computed(() =>
  effective.value ? fmtModulesZh(spec.value, effective.value.effective_modules) : [],
)
function moduleHasAddon(key: string): boolean {
  return effective.value?.addon_sources?.modules?.[key] !== undefined
}
const addonHintLicense = computed(() =>
  lic.value && effective.value
    ? { quotas: lic.value.quotas, addon_sources: effective.value.addon_sources }
    : null,
)
const activeAddons = computed(() => addons.value.filter((a) => a.status === 'active') as LicenseAddon[])

// --- 回報指標 ---
const metricRows = computed(() =>
  buildMetricRows(detail.value?.metric_definitions ?? [], site.value?.product ?? null, detail.value?.metrics),
)

// --- 動作：同期限改內容／簽發子證 ---
const contentOpen = ref(false)
const addonOpen = ref(false)
const canEditContent = computed(
  () => auth.isAdminOrAbove && sections.value.license && !!lic.value && siteLive.value && !!spec.value,
)
const canIssueAddon = computed(
  () => auth.isAdminOrAbove && sections.value.addons && !!site.value?.customer_id && siteLive.value && !!spec.value,
)
function contentDisabledReason(): string | undefined {
  if (!siteLive.value) return '站台已回收或回收中'
  if (!lic.value) return '這一站目前沒有生效中的授權'
  if (!spec.value) return '產品的憑證設計還沒載入'
  return undefined
}

async function onContentDone(res: SiteLicenseContentResult) {
  contentOpen.value = false
  const changed = (Object.keys(res.changes ?? {}) as ContentField[])
    .map((k) => CONTENT_FIELD_LABELS[k] ?? k)
    .join('、')
  await load()
  await dialogs.notify({
    title: '已重簽授權',
    message:
      `新授權 ${res.license.license_id.slice(0, 8)} 已取代 ${res.superseded.slice(0, 8)}（更新：${changed || '—'}）。` +
      'agent 下一輪心跳會把新授權寫進站台。',
  })
}
async function onAddonIssued() {
  addonOpen.value = false
  await load()
}
/** `AddonIssueModal` 吃的是〈授權〉清單的證型別；站台詳情的摘要投影欄位是它的子集合。 */
const addonLicense = computed(() => (lic.value ? (lic.value as unknown as License) : null))

// --- 站台機密 ---
const secretField = ref<SiteSecretField | null>(null)
const secretOpen = ref(false)
/**
 * 進行中的動作（`<動作>:<欄位 key>`；全部重送是 `redeliver:*`）——spinner 只轉在按下的那一顆
 * （原本清除與重送共用欄位 key，按清除時轉的是隔壁的重送）。
 */
const secretBusyKey = ref<string | null>(null)
/** 最近一次寫入派出的任務（畫面上給連結；agent 寫入後套用狀態才會變）。 */
const lastJob = ref<HostJob | null>(null)
const canManageSecrets = computed(() => auth.isManagerOrAbove && sections.value.site_secrets)

/** 寫入動作整體為什麼做不了（`null`＝可以）；按鈕反灰時的說明。 */
const secretWriteBlocker = computed<string | null>(() => {
  const s = secrets.value
  if (!s) return null
  if (!s.secret_key_available) return '授權中心尚未設定加密主鑰（IPO_LS_SITE_SECRET_KEY），站台機密功能暫停。'
  if (!s.vendor_host) return '這台是代理商自備的主機，我方的站台機密不能放到代理商的機器上。'
  if (!s.agent_supported) return '這台主機的 agent 不支援站台機密（請先升級 agent，或在〈代管種類〉登錄最低版本）。'
  if (!s.can_dispatch) return '只有「運行中」或「已停站」、且沒有進行中開站任務的站台能寫入站台機密。'
  return null
})
/** 清除只需要站台可派工＋agent 支援（不需要加密主鑰、代理商主機也清得掉）。 */
const clearBlocker = computed<string | null>(() => {
  const s = secrets.value
  if (!s) return null
  if (!s.agent_supported) return '這台主機的 agent 不支援站台機密。'
  if (!s.can_dispatch) return '只有「運行中」或「已停站」、且沒有進行中開站任務的站台能派工。'
  return null
})

function openSecret(f: SiteSecretField) {
  secretField.value = f
  secretOpen.value = true
}
function applyWrite(res: SiteSecretWriteResult) {
  if (detail.value) detail.value = { ...detail.value, site_secrets: res.secrets }
  if (res.job) lastJob.value = res.job
}
/**
 * 站台機密動作失敗後（批151 R3-15）：**一律重讀**這一站的狀態，再告訴操作者。
 *
 * 失敗常常就是「狀態變了」——別人剛改過（`secret_conflict`）、站台不能派工了、主鑰沒了、
 * 請求逾時但其實已寫入；畫面停在舊狀態，下一顆按鈕就又按在錯的前提上。重讀與提示同時進行
 * （不必等使用者關掉提示才更新底下的表格）。
 */
async function afterSecretFailure(title: string, e: unknown, fallback: string) {
  const reloading = load()
  await dialogs.notify({ title, message: siteSecretErrText(e, fallback) })
  await reloading
}
/** 設定／更換框送出失敗：框不關（錯誤顯示在框內），底下重讀，框裡的欄位換成新讀到的那一份。 */
async function onSecretFailed() {
  await load()
  const key = secretField.value?.key
  const fresh = key ? secrets.value?.fields.find((f) => f.key === key) : undefined
  if (fresh && secretOpen.value) secretField.value = fresh
}
async function onSecretDone(res: SiteSecretWriteResult) {
  secretOpen.value = false
  applyWrite(res)
  if (!res.changed) {
    await dialogs.notify({
      title: '值沒有變',
      message: '輸入的值與現行的相同，沒有寫入也沒有派工。要把同一把值重新寫進站台，請用〈重送〉。',
    })
  }
}
async function clearSecret(f: SiteSecretField) {
  if (!site.value) return
  const ok = await dialogs.confirm({
    title: '清除站台機密',
    message:
      `確定清除站台「${site.value.slug}」的「${f.label}」（${f.env}${f.last4 ? `，末四碼 ${f.last4}` : ''}）？\n\n` +
      '清除後授權中心立刻刪除這把值並釋出（可以給別站使用），並派工把它從站台的設定拿掉；' +
      '站台裡依賴它的功能會失效。這個動作無法復原，要再用必須重新輸入。',
    confirmText: '清除',
    danger: true,
  })
  if (!ok) return
  secretBusyKey.value = `clear:${f.key}`
  try {
    applyWrite(await sitesApi.deleteSiteSecret(site.value.id, f.key))
  } catch (e) {
    await afterSecretFailure('清除失敗', e, '清除失敗')
  } finally {
    secretBusyKey.value = null
  }
}
/**
 * 清除站上殘留（裁示 126①）：授權中心沒有值、但站台 `.env` 可能還留著那一行（例如開站失敗時
 * 已先釋出）。同一支 DELETE——後端在列不在時再派一次 clear。
 */
async function clearResidue(f: SiteSecretField) {
  if (!site.value) return
  const ok = await dialogs.confirm({
    title: '清除站上殘留',
    message: clearResidueConfirmMessage(site.value.slug, f),
    confirmText: '派工清除',
    danger: true,
  })
  if (!ok) return
  secretBusyKey.value = `residue:${f.key}`
  try {
    applyWrite(await sitesApi.deleteSiteSecret(site.value.id, f.key))
  } catch (e) {
    await afterSecretFailure('清除失敗', e, '清除失敗')
  } finally {
    secretBusyKey.value = null
  }
}
/**
 * 只釋出、不派工（裁示 126②）：只刪授權中心的紀錄並釋出唯一性，站台上的值不動。
 * 開站卡住或站台已不再使用時用（站台不能派工也做得了，所以不看 `clearBlocker`）。
 */
async function releaseSecret(f: SiteSecretField) {
  if (!site.value) return
  const ok = await dialogs.confirm({
    title: '只釋出站台機密（不派工）',
    message: releaseConfirmMessage(site.value.slug, f),
    confirmText: '只釋出',
    danger: true,
  })
  if (!ok) return
  secretBusyKey.value = `release:${f.key}`
  try {
    const res = await sitesApi.releaseSiteSecret(site.value.id, f.key)
    if (detail.value) detail.value = { ...detail.value, site_secrets: res.secrets }
  } catch (e) {
    // 「沒有紀錄可釋出」不是 `changed: false`（後端成功一律 `changed: true`），而是 404
    // `secret_not_set`——畫面上還掛著〈只釋出〉＝狀態舊了（別人剛清除／釋出）。重讀之後按鈕會消失。
    if (apiDetail(e).code === 'secret_not_set') {
      const reloading = load()
      await dialogs.notify({
        title: '沒有可釋出的紀錄',
        message: `授權中心已經沒有「${f.label}」的紀錄（可能剛被其他人清除或釋出），不需要再釋出；畫面已重新整理。`,
      })
      await reloading
    } else {
      await afterSecretFailure('釋出失敗', e, '釋出失敗')
    }
  } finally {
    secretBusyKey.value = null
  }
}
async function redeliver(f: SiteSecretField | null) {
  if (!site.value) return
  const what = f ? `「${f.label}」` : '全部已設定的站台機密'
  const ok = await dialogs.confirm({
    title: '重送站台機密',
    message:
      `把${what}的現行值重新寫進站台「${site.value.slug}」？\n\n` +
      '用在上次套用失敗、或站台重裝之後。值不變，只是再派一次寫入任務。',
    confirmText: '重送',
  })
  if (!ok) return
  secretBusyKey.value = `redeliver:${f ? f.key : '*'}`
  try {
    applyWrite(await sitesApi.redeliverSiteSecrets(site.value.id, f ? [f.key] : undefined))
  } catch (e) {
    await afterSecretFailure('重送失敗', e, '重送失敗')
  } finally {
    secretBusyKey.value = null
  }
}
const configuredCount = computed(() => (secrets.value?.fields ?? []).filter((f) => f.configured).length)
/** 每列要顯示哪些按鈕（營運主管以上才有；admin 只看狀態）。 */
function rowActions(f: SiteSecretField) {
  return secretRowActions(f, canManageSecrets.value)
}

function fmt(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '—'
}
</script>

<template>
  <div class="page">
    <div class="crumb">
      <RouterLink v-if="detail?.host" :to="{ name: 'host-detail', params: { id: detail.host.id } }">← 主機 {{ detail.host.name }}</RouterLink>
      <RouterLink v-else :to="{ name: 'hosts' }">← 代管主機</RouterLink>
      <span class="sep">·</span>
      <RouterLink :to="{ name: 'sites' }">全部站台</RouterLink>
    </div>

    <div v-if="loading && !detail" class="loading-wrap"><span class="spinner lg" /></div>
    <div v-else-if="loadError && !detail" class="card"><p class="err">{{ loadError }}</p></div>

    <template v-else-if="detail && site">
      <div class="page-head">
        <h1>{{ site.slug }}</h1>
        <span class="status-pill" :class="siteStateClass(site.state)">{{ siteStateLabel(site.state) }}</span>
        <a v-if="safeHttpUrl(site.public_url)" class="dom" :href="safeHttpUrl(site.public_url) ?? undefined" target="_blank" rel="noopener">{{ site.domain ?? site.public_url }}</a>
        <span v-else-if="site.public_url" class="dom">{{ site.domain ?? site.public_url }}</span>
        <span v-else-if="site.domain" class="dom">{{ site.domain }}</span>
        <span class="spacer" />
        <template v-if="auth.isAdminOrAbove">
          <UiButton
            variant="secondary"
            :disabled="!canEditContent"
            :title="canEditContent ? '只改模組／配額／行為參數，期限與套裝沿用' : contentDisabledReason()"
            @click="contentOpen = true"
          >同期限改內容</UiButton>
          <UiButton
            variant="secondary"
            :disabled="!canIssueAddon"
            :title="canIssueAddon ? undefined : site.customer_id ? contentDisabledReason() : '這一站沒有歸屬客戶'"
            @click="addonOpen = true"
          >簽發子證</UiButton>
        </template>
      </div>
      <p v-if="loadError" class="err">{{ loadError }}（顯示的是最後一次成功讀取的資料）</p>

      <div class="grid2">
        <!-- 站台基本 -->
        <div class="card">
          <div class="card-head"><h3 class="section-title">站台</h3></div>
          <div class="kv">
            <div class="mi"><span class="mk">產品</span><span class="mv">{{ productLabel }}</span></div>
            <div class="mi"><span class="mk">instance</span><span class="mv"><code class="mono">{{ site.instance_id }}</code></span></div>
            <div class="mi"><span class="mk">版本</span>
              <span class="mv">
                {{ site.app_version ?? '—' }}
                <span v-if="site.version_mismatch" class="warnline">≠ 心跳回報 {{ site.cs_app_version ?? '—' }}</span>
              </span>
            </div>
            <div class="mi"><span class="mk">健康</span><span class="mv">{{ healthLabel(site.health) }}</span></div>
            <div class="mi"><span class="mk">憑證</span>
              <span class="mv">{{ tlsStateLabel(site.tls_state) }}<template v-if="site.cert_expires_at">（到期 {{ fmtSeen(site.cert_expires_at) }}）</template></span>
            </div>
            <div class="mi"><span class="mk">出貨 channel</span><span class="mv">{{ site.update_channel ?? '—' }}</span></div>
            <div class="mi"><span class="mk">最近心跳</span><span class="mv">{{ fmtSeen(site.last_seen) }}</span></div>
            <div class="mi"><span class="mk">建立時間</span><span class="mv">{{ fmtSeen(site.created_at) }}</span></div>
            <div class="mi"><span class="mk">來源</span><span class="mv">{{ site.origin === 'discovered' ? '盤點發現（非中台開的）' : '中台開站' }}</span></div>
          </div>
        </div>

        <!-- 主機與客戶 -->
        <div class="card">
          <div class="card-head"><h3 class="section-title">主機與客戶</h3></div>
          <div class="kv">
            <div class="mi"><span class="mk">客戶</span><span class="mv">{{ detail.customer?.name ?? '—（未歸屬客戶）' }}</span></div>
            <template v-if="detail.host">
              <div class="mi"><span class="mk">主機</span>
                <span class="mv"><RouterLink :to="{ name: 'host-detail', params: { id: detail.host.id } }">{{ detail.host.name }}</RouterLink></span>
              </div>
              <div class="mi"><span class="mk">主機狀態</span><span class="mv">{{ hostStatusLabel(detail.host.status) }}</span></div>
              <div class="mi"><span class="mk">對外位址</span><span class="mv">{{ detail.host.public_ip || detail.host.fqdn || '—' }}</span></div>
              <div class="mi"><span class="mk">agent</span>
                <span class="mv">{{ detail.host.agent_kind ?? '—' }} {{ detail.host.agent_version ?? '' }}</span>
              </div>
              <div class="mi"><span class="mk">主機歸屬</span>
                <span class="mv">{{ detail.host.reseller_id ? `代理商：${detail.host.reseller_name ?? '—'}` : '我方自營' }}</span>
              </div>
              <div class="mi"><span class="mk">agent 心跳</span><span class="mv">{{ fmtSeen(detail.host.last_seen) }}</span></div>
            </template>
          </div>
        </div>
      </div>

      <!-- 目前的證 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">目前的證</h3>
          <span class="spacer" />
          <span v-if="lic" class="subtle">簽發來源：{{ issuedViaLabel(lic.issued_via) }}</span>
        </div>
        <p v-if="!sections.license" class="denied">需要 admin 以上權限才看得到授權內容。</p>
        <p v-else-if="!lic" class="subtle">這一站目前沒有生效中的授權。</p>
        <template v-else>
          <div class="lic-head">
            <span class="status-pill" :class="effectiveStatus(lic)">{{ statusLabel(effectiveStatus(lic)) }}</span>
            <strong>{{ lic.plan_label ?? planLabel(spec, lic.plan) }}</strong>
            <span v-if="lic.trial" class="chip">試用</span>
            <code class="mono" :title="lic.license_id">{{ lic.license_id.slice(0, 8) }}</code>
            <span class="spacer" />
            <span class="term">
              <span v-if="lic.not_before" class="chip">{{ fmt(lic.not_before) }} 起生效</span>
              到期 {{ lic.expires_at ? fmt(lic.expires_at) : '首次取證才起算' }}
              <small v-if="formatRemaining(lic.expires_at)" class="subtle">（{{ formatRemaining(lic.expires_at) }}）</small>
              <small class="subtle">・期限 {{ termText(lic) }}</small>
            </span>
          </div>
          <section class="sec">
            <h4>模組</h4>
            <div class="chips">
              <span v-for="m in licModules" :key="m.key" class="chip">{{ m.text }}</span>
              <span v-if="!licModules.length" class="subtle">—</span>
            </div>
          </section>
          <section class="sec">
            <h4>配額（證上的值）</h4>
            <QuotaChips :spec="spec" :quotas="lic.quotas" />
          </section>
          <section v-if="policyRows.length" class="sec">
            <h4>行為參數</h4>
            <div class="chips">
              <span v-for="p in policyRows" :key="p.key" class="chip" :class="{ custom: p.custom }">{{ p.label }} {{ p.text }}</span>
            </div>
          </section>
        </template>
      </div>

      <!-- 子證與合併後配額 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">子證與合併後配額</h3>
          <span class="spacer" />
          <span v-if="sections.addons" class="subtle">生效中 {{ addons.filter((a) => a.in_effect).length }} 張</span>
        </div>
        <p v-if="!sections.addons" class="denied">需要 admin 以上權限才看得到子證與合併後配額。</p>
        <template v-else>
          <template v-if="effective">
            <section class="sec first">
              <h4>合併後模組</h4>
              <div class="chips">
                <span
                  v-for="m in effModules"
                  :key="m.key"
                  class="chip"
                  :class="{ 'has-addon': moduleHasAddon(m.key) }"
                  :title="moduleHasAddon(m.key) && effective ? moduleAddonHint(effective, m.key) : undefined"
                >{{ m.text }}<small v-if="moduleHasAddon(m.key)" class="ad-tag">＋加購</small></span>
                <span v-if="!effModules.length" class="subtle">—</span>
              </div>
            </section>
            <section class="sec">
              <h4>合併後配額<small class="subtle">（依產品宣告的合併方式；含用量）</small></h4>
              <QuotaChips
                :spec="spec"
                :quotas="effective.effective_quotas"
                :usage="detail.usage"
                :license="addonHintLicense"
              />
            </section>
          </template>
          <p v-else class="subtle">沒有生效中的主證，無法計算合併後配額。</p>

          <section class="sec">
            <h4>子證清單</h4>
            <p v-if="!addons.length" class="subtle">這一站沒有子證（含近 30 天撤銷／取代的紀錄）。</p>
            <div v-else class="tbl-scroll" data-hscroll>
              <table v-card-table class="tbl card-table mini">
                <thead><tr><th>子證</th><th>內容</th><th>狀態</th><th>到期</th><th>來源</th></tr></thead>
                <tbody>
                  <tr v-for="a in addons" :key="a.addon_id" :class="{ dim: !a.in_effect }">
                    <td>
                      <strong>{{ a.label }}</strong>
                      <span v-if="a.trial" class="chip">試用</span>
                      <div><code class="mono" :title="a.addon_id">{{ a.addon_id.slice(0, 8) }}</code></div>
                    </td>
                    <td class="small">{{ addonSummary(a, spec) }}</td>
                    <td>
                      <span class="status-pill" :class="a.status">{{ addonStatusLabel(a) }}</span>
                      <div v-if="a.status === 'active' && !a.in_effect" class="sub subtle">未計入合併</div>
                    </td>
                    <td class="nowrap">
                      {{ a.expires_at ? fmt(a.expires_at) : '首次下發才起算' }}
                      <div v-if="formatRemaining(a.expires_at)" class="sub subtle">{{ formatRemaining(a.expires_at) }}</div>
                    </td>
                    <td class="small">
                      {{ addonSourceLabel(a.source) }}
                      <div v-if="a.issued_by" class="sub subtle">{{ a.issued_by }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>
      </div>

      <!-- 回報指標 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">回報指標</h3>
          <span class="spacer" />
          <span class="subtle">{{ detail.metrics_at ? `回報時間 ${fmtSeen(detail.metrics_at)}` : '' }}</span>
        </div>
        <p v-if="!metricRows.length" class="subtle">
          {{ detail.metrics ? '這一站回報的指標都設成不顯示。' : '這一站還沒有回報過指標。' }}
        </p>
        <div v-else class="metrics">
          <div v-for="m in metricRows" :key="m.key" class="metric" :title="m.hint ?? undefined">
            <span class="m-label">{{ m.label }}<small v-if="m.unknown" class="subtle">（未定義顯示名稱）</small></span>
            <span class="m-val">{{ m.text }}</span>
          </div>
        </div>
      </div>

      <!-- 站台機密 -->
      <div class="card">
        <div class="card-head">
          <h3 class="section-title">站台機密</h3>
          <span class="spacer" />
          <UiButton
            v-if="canManageSecrets && configuredCount > 1"
            size="sm"
            :disabled="!!secretWriteBlocker"
            :loading="secretBusyKey === 'redeliver:*'"
            :title="secretWriteBlocker ?? undefined"
            @click="redeliver(null)"
          >全部重送</UiButton>
        </div>
        <p v-if="!sections.site_secrets" class="denied">需要 admin 以上權限才看得到站台機密的狀態。</p>
        <template v-else-if="secrets">
          <p v-if="!secrets.declared && !secrets.fields.length" class="subtle">這個產品沒有宣告站台機密欄位。</p>
          <template v-else>
            <p class="subtle lead">
              值由授權中心加密保存，後台只看得到末四碼與套用狀態；
              <template v-if="canManageSecrets">
                設定、更換、清除與重送會派任務給主機 agent 寫進站台；「只釋出」只在授權中心刪除紀錄、不派工。
              </template>
              <template v-else>設定、更換、清除、重送與釋出需要「營運主管」以上。</template>
            </p>
            <p v-if="secretWriteBlocker" class="notice">{{ secretWriteBlocker }}</p>
            <p v-if="lastJob" class="subtle lead">
              已派出寫入任務
              <RouterLink :to="{ name: 'job-detail', params: { jobId: lastJob.job_id } }"><code class="mono">{{ lastJob.job_id }}</code></RouterLink>
              ——agent 寫入完成後「套用狀態」才會更新（頁面每 15 秒自動重新整理）。
            </p>
            <div class="tbl-scroll" data-hscroll>
              <table v-card-table class="tbl card-table">
                <thead>
                  <tr><th>欄位</th><th>環境變數</th><th>已設定</th><th>版本</th><th>套用狀態</th><th v-if="canManageSecrets"></th></tr>
                </thead>
                <tbody>
                  <tr v-for="f in secrets.fields" :key="f.key">
                    <td>
                      <strong>{{ f.label }}</strong>
                      <span v-if="!f.declared" class="chip" title="產品的代管設定已拿掉這個欄位，但值還存著；只能清除">宣告已移除</span>
                      <div v-if="f.hint" class="sub subtle">{{ f.hint }}</div>
                    </td>
                    <td><code class="mono">{{ f.env }}</code></td>
                    <td class="nowrap">{{ secretLast4Text(f) }}</td>
                    <td class="nowrap">
                      {{ f.configured ? secretVersionText(f.version) : '—' }}
                      <div v-if="f.set_by" class="sub subtle">{{ f.set_by }}</div>
                    </td>
                    <td>
                      <span class="ap" :class="secretAppliedText(f).tone">{{ secretAppliedText(f).text }}</span>
                    </td>
                    <td v-if="canManageSecrets" class="acts-cell">
                      <div class="acts">
                        <UiButton
                          v-if="rowActions(f).set"
                          size="sm"
                          :disabled="!!secretWriteBlocker || !siteLive"
                          :title="secretWriteBlocker ?? undefined"
                          @click="openSecret(f)"
                        >{{ f.configured ? '更換' : '設定' }}</UiButton>
                        <UiButton
                          v-if="rowActions(f).redeliver"
                          size="sm"
                          :disabled="!!secretWriteBlocker"
                          :loading="secretBusyKey === `redeliver:${f.key}`"
                          :title="secretWriteBlocker ?? undefined"
                          @click="redeliver(f)"
                        >重送</UiButton>
                        <UiButton
                          v-if="rowActions(f).clear"
                          size="sm"
                          variant="danger"
                          :disabled="!!clearBlocker"
                          :loading="secretBusyKey === `clear:${f.key}`"
                          :title="clearBlocker ?? undefined"
                          @click="clearSecret(f)"
                        >清除</UiButton>
                        <!-- 裁示 126①：授權中心沒有值，但站台 .env 可能還留著那一行 → 派一次 clear。 -->
                        <UiButton
                          v-if="rowActions(f).clearResidue"
                          size="sm"
                          variant="danger"
                          :disabled="!!clearBlocker"
                          :loading="secretBusyKey === `residue:${f.key}`"
                          :title="clearBlocker ?? '派工請 agent 把站台 .env 的這一行刪掉'"
                          @click="clearResidue(f)"
                        >清除站上殘留</UiButton>
                        <!-- 裁示 126②：只刪授權中心的紀錄並釋出唯一性，不派工（站台不能派工時也做得了）。 -->
                        <UiButton
                          v-if="rowActions(f).release"
                          size="sm"
                          variant="warn"
                          :loading="secretBusyKey === `release:${f.key}`"
                          title="只在授權中心刪除紀錄並釋出唯一性，站台上的值不會被移除"
                          @click="releaseSecret(f)"
                        >只釋出（不派工）</UiButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </template>
      </div>
    </template>

    <SiteContentModal
      :open="contentOpen"
      :site-id="siteId"
      :site-slug="site?.slug ?? ''"
      :license="lic"
      :spec="spec"
      @close="contentOpen = false"
      @done="onContentDone"
    />
    <AddonIssueModal
      v-if="site?.customer_id"
      :open="addonOpen"
      :customer-id="site.customer_id"
      :instance-id="site.instance_id"
      :license="addonLicense"
      :product="site.product"
      :spec="spec"
      :active-addons="activeAddons"
      :allow-internal="auth.isManagerOrAbove"
      @close="addonOpen = false"
      @issued="onAddonIssued"
    />
    <SiteSecretModal
      :open="secretOpen"
      :site-id="siteId"
      :site-slug="site?.slug ?? ''"
      :field="secretField"
      @close="secretOpen = false"
      @done="onSecretDone"
      @failed="onSecretFailed"
    />
  </div>
</template>

<style scoped>
.crumb {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: var(--sp-3);
  font-size: var(--fs-12);
}
.crumb a {
  color: var(--fg-3);
  text-decoration: none;
}
.crumb a:hover {
  color: var(--fg);
}
.sep {
  color: var(--fg-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.dom {
  font-size: var(--fs-13);
  color: var(--fg-3);
}
.spacer {
  flex: 1;
}
.err {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.grid2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: var(--sp-4);
  margin-bottom: var(--sp-4);
}
.grid2 > .card + .card {
  margin-top: 0;
}
.kv {
  display: grid;
  gap: 6px;
}
.mi {
  display: flex;
  gap: var(--sp-3);
  font-size: var(--fs-13);
}
.mk {
  flex: 0 0 96px;
  color: var(--fg-3);
}
.mv {
  color: var(--fg);
  word-break: break-word;
  min-width: 0;
}
.warnline {
  margin-left: 6px;
  color: var(--warning);
  font-size: var(--fs-12);
}
.denied {
  margin: 0;
  padding: var(--sp-3);
  border: 1px dashed var(--border-strong, var(--border));
  border-radius: var(--r-md);
  color: var(--fg-3);
  font-size: var(--fs-13);
  text-align: center;
}
.lic-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--sp-2);
  padding-bottom: var(--sp-3);
  border-bottom: 1px solid var(--border-2, var(--border));
}
.term {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.sec {
  margin-top: var(--sp-3);
}
.sec.first {
  margin-top: 0;
}
.sec h4 {
  margin: 0 0 6px;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.sec h4 small {
  margin-left: 6px;
  font-weight: 400;
}
.chip.custom {
  border-color: var(--accent-500);
}
.chip.has-addon {
  border-color: var(--brand-600, var(--border-1));
}
.ad-tag {
  margin-left: 4px;
  color: var(--brand-600, var(--fg-3));
  font-size: var(--fs-10, 10px);
  font-weight: 600;
  cursor: help;
}
.tbl.mini {
  font-size: var(--fs-12);
}
tr.dim td {
  opacity: 0.6;
}
.small {
  font-size: var(--fs-12);
}
.sub {
  font-size: var(--fs-11);
}
.nowrap {
  white-space: nowrap;
}
.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--sp-2);
}
.metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
}
.m-label {
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.m-val {
  font-size: var(--fs-16, 16px);
  font-weight: 600;
  color: var(--fg);
}
.lead {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-12);
}
.notice {
  margin: 0 0 var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--warning);
  border-radius: var(--r-sm, 6px);
  color: var(--warning);
  font-size: var(--fs-12);
}
.ap {
  font-size: var(--fs-12);
}
.ap.ok {
  color: var(--success, var(--brand-600));
}
.ap.pending {
  color: var(--warning);
}
.ap.none {
  color: var(--fg-4);
}
</style>
